/*
 * ── Unified Sidebar Menu Configuration ──
 *
 * SINGLE SOURCE OF TRUTH for all sidebar navigation items.
 * Instead of maintaining separate nav arrays per role, every item
 * declares its own visibility rules via metadata.
 *
 * ─── PRODUCT SECTIONS (company/planner) ───
 *
 *   Command Deck     Primary home surface — cross-event monitoring dashboard
 *                    (renamed from "Control Center" in Tier 5B Pass 3 to
 *                    disambiguate from the per-event "Event Control" tab)
 *   Sales            Pre-booking workflow (leads, pipeline, automation)
 *   Core             Booked-event planning and budgets
 *   Ops              Staffing, availability, announcements, GPS, chat, leaderboard
 *   Network          Reusable relationships — clients, locations, vendors, venues
 *   System           Company config, billing
 *   Admin            Platform admin (gated)
 *
 * ─── KEY CONCEPTS ───
 *
 *   role          Backend identity stored on the user document.
 *                 Values: manager, staff, vendor, planner, admin, member
 *                 Set during registration. Do NOT change at runtime.
 *
 *   industryType  Business context stored on the user document.
 *                 Values: dj_company, event_planner, vendor, staffing_company
 *                 Determines which company features are relevant.
 *
 *   workspace     Frontend-only view context derived from role/flags.
 *                 Values: company, staff, vendor, planner
 *                 This is what the sidebar filters on.
 *
 * ─── VISIBILITY RULES (evaluated by sidebarFilter.js) ───
 *
 *   allowedWorkspaces     Show item only in these workspaces. Omit = show in all.
 *   allowedRoles          Show item only for these backend roles. Omit = no restriction.
 *   allowedIndustryTypes  Show item only for these industries. Omit = no restriction.
 *   hideForRoles          Hide item for these roles (e.g. 'member' can't see billing).
 *   hideForIndustryTypes  Hide item for these industries. Reserved for future use.
 *   adminOnly             Requires isAdmin / isSuperAdmin / platform admin email.
 *   platformOwnerOnly     Requires user email to be in PLATFORM_ADMIN_EMAILS (STAFFED-only).
 *   featureFlag           Placeholder — will gate on feature flags when implemented.
 *
 * ─── HOW TO EXTEND ───
 *
 *   Add a new item:       Append to SIDEBAR_MENU with correct workspace/role tags.
 *   Add a new workspace:  Update resolveWorkspace() in sidebarFilter.js, tag items.
 *   Add a new role:       Tag relevant items, filtering function handles the rest.
 *   No need to touch Sidebar.jsx for any of the above.
 */

import {
  Calendar,
  Users,
  Settings,
  MapPin,
  DollarSign,
  UserCircle,
  Flame,
  Megaphone,
  ClipboardList,
  Building2,
  UsersRound,
  Heart,
  CreditCard,
  Radio,
  QrCode,
  Code,
  UserCog,
  Store,
  Home,
  CalendarOff,
  Shield,
  Zap,
  Columns3,
  Inbox,
  Wrench,
  Globe,
  MessageCircle,
  Navigation,
  FileText,
  Star,
  ShieldAlert,
  Layers,
  LayoutDashboard,
  CalendarRange,
  CalendarClock,
  Banknote,
  ShieldCheck,
  CreditCard as CreditCardIcon,
} from 'lucide-react';


const SIDEBAR_MENU = [

  /* ────────────────────────────────────────────
   * STAFF WORKSPACE
   * Visible only when workspace = 'staff'
   * ──────────────────────────────────────────── */

  {
    id: 'portal',
    label: 'Home',
    icon: Home,
    path: '/staff-portal',
    isTopLevel: true,
    allowedWorkspaces: ['staff'],
  },
  {
    id: 'my-assignments',
    label: 'My Assignments',
    icon: ClipboardList,
    path: '/staff/my-assignments',
    isTopLevel: true,
    allowedWorkspaces: ['staff'],
    flag: 'staff_assignments_briefs',
  },
  {
    id: 'my-availability',
    label: 'My Availability',
    icon: CalendarOff,
    path: '/staff/my-availability',
    isTopLevel: true,
    allowedWorkspaces: ['staff'],
    flag: 'staff_availability',
  },
  {
    id: 'staff-announcements',
    label: 'Announcements',
    icon: Megaphone,
    path: '/staff/announcements',
    isTopLevel: true,
    allowedWorkspaces: ['staff'],
    flag: 'announcements_bulletin',
  },
  {
    id: 'staff-leaderboard',
    label: 'Leaderboard',
    icon: Flame,
    path: '/staff/leaderboard',
    isTopLevel: true,
    allowedWorkspaces: ['staff'],
    experimental: true,
  },


  /* ══════════════════════════════════════════════
   * COMPANY + PLANNER WORKSPACE
   * The 7 product sections for the core platform
   * ══════════════════════════════════════════════ */


  /* ────────────────────────────────────────────
  /* ────────────────────────────────────────────
   * 1. COMMAND DECK
   * Primary home surface — cross-event monitoring dashboard.
   * (Renamed from "Control Center" in Tier 5B Pass 3 — the per-
   * event tab inside the workspace is now labelled "Event Control"
   * to remove the long-standing naming collision.)
   * ──────────────────────────────────────────── */

  {
    id: 'control-center',
    label: 'Command Deck',
    icon: LayoutDashboard,
    path: '/control-center',
    isTopLevel: true,
    allowedWorkspaces: ['company', 'planner'],
    flag: 'command_deck',
  },


  /* ────────────────────────────────────────────
   * 2. SALES
   * Pre-booking workflow: leads, pipeline, automation
   * ──────────────────────────────────────────── */

  {
    id: 'inbox',
    label: 'SALES',
    icon: Inbox,
    allowedWorkspaces: ['company', 'planner'],
    children: [
      { label: 'Pipeline',   icon: Columns3,   path: '/events/pipeline', flag: 'pipeline_basic' },
      { id: 'live-leads', label: 'Live Leads', icon: Radio,      path: '/live-leads', flag: 'live_leads' },
      { label: 'QR Codes',   icon: QrCode,     path: '/qr-codes', flag: 'qr_codes' },
    ],
  },


  /* ────────────────────────────────────────────
   * 3. CORE
   * Booked-event planning and budgets
   * ──────────────────────────────────────────── */

  {
    id: 'events',
    label: 'Events',
    icon: CalendarRange,
    allowedWorkspaces: ['company', 'planner'],
    children: [
      { id: 'events-list', label: 'Events',  icon: Calendar,   path: '/events', exact: true, flag: 'events_workspace' },
      { label: 'Budgets', icon: DollarSign,  path: '/events/budgets', flag: 'budgets' },
      { label: 'Reviews', icon: Star, path: '/reviews' },
    ],
  },

  /* ────────────────────────────────────────────
   * CLIENTS — top-level entry (moved out of the old Network group),
   * styled like Command Deck. Same route/flag as before; display only.
   * ──────────────────────────────────────────── */

  {
    id: 'clients',
    label: 'Clients',
    icon: UserCircle,
    path: '/events/clients',
    isTopLevel: true,
    allowedWorkspaces: ['company', 'planner'],
    flag: 'clients_crm',
  },


  /* ────────────────────────────────────────────
   * 4. OPS
   * Staffing, live execution tools, communication
   * ──────────────────────────────────────────── */

  {
    id: 'ops',
    label: 'Team',
    icon: Wrench,
    allowedWorkspaces: ['company', 'planner'],
    children: [
      { label: 'Staff',              icon: Users,          path: '/staff', flag: 'staff_roster' },
      { label: 'Availability',       icon: CalendarClock,  path: '/staff/availability', flag: 'staff_availability' },
      { label: 'Roles',              icon: UserCog,        path: '/staff/roles', flag: 'staff_roles' },
      { label: 'Announcements',      icon: Megaphone,      path: '/staff/announcement', flag: 'announcements' },
      { id: 'payouts', label: 'Payouts',            icon: Banknote,       path: '/payouts', flag: 'payouts' },
      { label: 'Heat Leaderboard',   icon: Flame,          path: '/staff/leaderboard', experimental: true, flag: 'staff_performance' },
    ],
  },


  /* ────────────────────────────────────────────
   * 5. NETWORK
   * Reusable relationships: locations, vendors, venues
   * ──────────────────────────────────────────── */

  {
    id: 'network',
    label: 'Vendors & Venues',
    icon: Globe,
    allowedWorkspaces: ['company', 'planner'],
    children: [
      { label: 'Venues',       icon: MapPin,     path: '/events/locations', flag: 'locations' },
      { label: 'My Vendors',   icon: Heart,      path: '/account/preferred-vendors', flag: 'preferred_vendors' },
      { label: 'Find Vendors', icon: Store,      path: '/planner', flag: 'live_directories' },
    ],
  },


  /* ────────────────────────────────────────────
   * 6. SYSTEM
   * Company configuration, billing
   * ──────────────────────────────────────────── */

  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    path: '/settings/setup',
    isTopLevel: true,
    allowedWorkspaces: ['company', 'planner', 'vendor'],
    hideForRoles: ['member'],
    flag: 'company_branding',
    tooltip: "Everything that makes STAFFED yours — profile, branding, packages, templates, billing and more.",
  },


  /* ────────────────────────────────────────────
   * 7. PLATFORM
   * Platform-owner tools, always visible under the PLATFORM
   * heading for whoever can see them today. Visibility rules on
   * each child are UNCHANGED from their prior top-level form.
   * ──────────────────────────────────────────── */

  {
    id: 'platform',
    label: 'PLATFORM',
    icon: Shield,
    allowedWorkspaces: ['company', 'planner'],
    children: [
      {
        id: 'admin',
        label: 'Admin Panel',
        icon: ShieldCheck,
        path: '/admin',
        exact: true,
        allowedWorkspaces: ['company', 'planner'],
        adminOnly: true,
        tooltip: 'Platform-wide tools. Only you can see this.',
      },
      {
        id: 'unattributed-leads',
        label: 'Unattributed Leads',
        icon: ShieldAlert,
        path: '/admin/unattributed-leads',
        allowedWorkspaces: ['company', 'planner'],
        platformOwnerOnly: true,
        flag: 'unattributed_leads',
        tooltip: 'Leads that arrived without enough detail to tell whose account they belong to. Review each one before claiming it.',
      },
      {
        id: 'entitlements',
        label: 'Plans & Features',
        icon: Layers,
        path: '/admin/entitlements',
        allowedWorkspaces: ['company', 'planner'],
        platformOwnerOnly: true,
        tooltip: "Set each tenant's plan, force any feature on or off, and control the beta-gate.",
      },
    ],
  },

];


/*
 * ── Workspace Section Ordering ──
 *
 * By default, sidebar sections render in SIDEBAR_MENU array order.
 * Workspaces listed here override that order for their users.
 *
 * Planner differentiation:
 *   - Sales (leads/pipeline) promoted higher (core planner workflow)
 *   - Network promoted (planners live in vendor/venue sourcing)
 *   - Ops de-prioritised (internal staffing, less daily for planners)
 */
export const WORKSPACE_SECTION_ORDER = {
  planner: [
    'control-center',     // Command Deck — primary home surface
    'inbox',              // Sales — leads & pipeline (core planner sales flow)
    'events',             // Core — booked events
    'clients',            // Clients — top-level CRM entry
    'ops',                // Staffing & execution tools
    'network',            // Vendors & venues
    'settings',           // System — company config
    'admin',              // Admin panel (if applicable)
  ],
};

export default SIDEBAR_MENU;
